import * as React from "react";
import { useMutation } from "react-query";
import { ApiKeyMtlsCertificateCreateRequest, ApiKeysService } from "gen/clients";
import * as Yup from "yup";
import { Form, Formik } from "formik";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack } from "@mui/material";
import { ApiErrorMessage } from "components/ApiErrorMessage/ApiErrorMessage";
import { LoadingButton } from "components/LoadingButton/LoadingButton";
import { TextInputField } from "components/formikFields/TextInputField/TextInputField";
import { SwitchField } from "components/formikFields/SwitchField/SwitchField";

interface Props {
  keyId: string;
  onClose: () => void;
  onSuccess: () => void;
}

enum FieldName {
  description = "description",
  certificatePem = "certificatePem",
  enabled = "enabled"
}

interface FormValues {
  [FieldName.description]: string;
  [FieldName.certificatePem]: string;
  [FieldName.enabled]: boolean;
}

const PEM_HEADER = "-----BEGIN CERTIFICATE-----";

const validationSchema = Yup.object().shape({
  [FieldName.description]: Yup.string(),
  [FieldName.certificatePem]: Yup.string()
    .required("Certificate is required")
    .test(
      "is-pem",
      `Certificate must be PEM-encoded and start with "${PEM_HEADER}"`,
      value => !!value && value.trim().startsWith(PEM_HEADER)
    ),
  [FieldName.enabled]: Yup.boolean()
});

const AddMtlsCertificateDialog: React.FC<Props> = ({ keyId, onClose, onSuccess }) => {
  const { mutateAsync, error, isLoading } = useMutation(ApiKeysService.addApiKeyMtlsCertificate, {
    onSuccess
  });

  const onSubmit = React.useCallback(
    async (values: FormValues) => {
      const request: ApiKeyMtlsCertificateCreateRequest = {
        description: values.description || undefined,
        certificatePem: values.certificatePem.trim(),
        enabled: values.enabled
      };
      await mutateAsync({ keyId, requestBody: request });
    },
    [keyId, mutateAsync]
  );

  return (
    <Dialog open={true} onClose={onClose} maxWidth="md" fullWidth={true}>
      <DialogTitle>Add Certificate</DialogTitle>
      <Formik
        initialValues={{
          [FieldName.description]: "",
          [FieldName.certificatePem]: "",
          [FieldName.enabled]: true
        }}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
      >
        {({ isSubmitting }) => (
          <Form noValidate={true}>
            <DialogContent>
              <Stack spacing={3}>
                <TextInputField
                  name={FieldName.description}
                  label="Description"
                  helperText="Optional description of this certificate"
                />
                <TextInputField
                  name={FieldName.certificatePem}
                  label="Certificate (PEM)"
                  required={true}
                  multiline={true}
                  minRows={10}
                  maxRows={20}
                  helperText="Paste the PEM-encoded public client certificate"
                  InputProps={{ sx: { fontFamily: "monospace", fontSize: "0.85rem" } }}
                />
                <SwitchField
                  name={FieldName.enabled}
                  label="Enabled"
                  helperText="Whether this certificate binding is active immediately"
                />
                {!!error && <ApiErrorMessage apiError={error} />}
              </Stack>
            </DialogContent>
            <DialogActions>
              <LoadingButton variant="contained" isLoading={isSubmitting || isLoading}>
                Add Certificate
              </LoadingButton>
              <Button onClick={onClose} disabled={isSubmitting || isLoading}>
                Cancel
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export { AddMtlsCertificateDialog };
